import { useEffect, useRef, type ReactNode } from 'react';
import { Portal } from './Portal';

/**
 * Anything that pops out over the page.
 *
 * There is one rule and it holds everywhere: clicking outside it closes it,
 * and so does Escape. Before this, every dialog wrote its own backdrop and
 * most of them forgot — so a half-filled event form could only be escaped by
 * finding the Cancel button, which is not how anything else on a computer
 * behaves.
 *
 * Made a component rather than a convention, because a convention is a thing
 * the next dialog forgets. If it pops out, it uses this.
 *
 * Three details that decide whether the outside click feels right or loses
 * somebody's work:
 *
 *  - It closes on pointer *down* and *up* both landing on the backdrop. A
 *    click that starts inside — selecting the end of a line of text and
 *    releasing past the edge of the panel — is not a click outside, and
 *    treating it as one throws away what they were typing.
 *
 *  - The backdrop is the click target, not the panel's parent by geometry, so
 *    a menu or a date picker the browser draws over us does not count.
 *
 *  - Focus moves into the dialog when it opens and returns to whatever had it
 *    when it closes, so keyboard and screen reader users are not left at the
 *    top of the page.
 */
export function Modal({
  onClose,
  label,
  children,
  /** Where the panel sits: centred by default, or against an edge. */
  align = 'center',
  className = '',
  /** Backdrop opacity class, for the rare dialog that wants a darker page. */
  backdrop = 'bg-espresso/30',
  zIndex = 50,
}: {
  onClose: () => void;
  label: string;
  children: ReactNode;
  align?: 'center' | 'top' | 'right';
  className?: string;
  backdrop?: string;
  zIndex?: number;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const startedOnBackdrop = useRef(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;

    // The first thing worth typing into, or the panel itself.
    const focusable = panel.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]), textarea, select, [contenteditable="true"], button',
    );
    focusable?.focus();

    return () => previous?.focus?.();
  }, []);

  const place =
    align === 'right'
      ? 'items-stretch justify-end'
      : align === 'top'
        ? 'items-start justify-center overflow-y-auto'
        : 'items-center justify-center';

  return (
    <Portal>
      <div
        className={`fixed inset-0 flex ${place} ${backdrop} ${align === 'right' ? '' : 'p-4'}`}
        style={{ zIndex }}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onPointerDown={(e) => {
          startedOnBackdrop.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          // Both ends of the click have to be outside. A drag that began on a
          // text field and finished out here is a selection, not a dismissal.
          if (e.target === e.currentTarget && startedOnBackdrop.current) onClose();
          startedOnBackdrop.current = false;
        }}
      >
        <div ref={panel} className={className} onPointerDown={(e) => e.stopPropagation()}>
          {children}
        </div>
      </div>
    </Portal>
  );
}
